/**
 * Shop page behaviour: the facet bar over the cup grid.
 *
 * Ported from components/sections/ShopFilters.tsx. The grid is rendered by
 * Liquid, one card per size, each `li.grid__item` carrying `data-size`
 * ("8oz") and `data-walls` ("single double"). The two facets narrow those
 * cards client side, exactly as the React state did:
 *
 *   - Wall: a card stays when the size offers any of the ticked walls
 *     (the facet narrows to the sizes that sell that wall rather than
 *     splitting the grid).
 *   - Size: a card stays when its size is ticked.
 *   - No tick in a facet means that facet does not filter.
 *
 * The "N selected" line in each dropdown, the "Remove all" button (only
 * shown while a filter is active) and the "N products" count follow.
 * Re-bound when the theme editor reloads the section.
 */

function selectedValues(facet) {
  return Array.from(facet.querySelectorAll('[data-facet-option]'))
    .filter((input) => input.checked)
    .map((input) => input.value);
}

function bind(root) {
  if (root.dataset.shopBound === '1') return;
  const section = root.closest('.shopify-section') || root.parentElement || document;
  const grid = section.querySelector('[data-shop-grid]');
  if (!grid) return;
  root.dataset.shopBound = '1';

  const facets = Array.from(root.querySelectorAll('[data-facet]'));
  const removeAll = root.querySelector('[data-facets-remove-all]');
  const count = root.querySelector('[data-product-count]');
  const cards = () => Array.from(grid.querySelectorAll('.grid__item'));

  const apply = () => {
    const wallFacet = facets.find((f) => f.dataset.facet === 'wall');
    const sizeFacet = facets.find((f) => f.dataset.facet === 'size');
    const walls = wallFacet ? selectedValues(wallFacet) : [];
    const sizes = sizeFacet ? selectedValues(sizeFacet) : [];

    let shown = 0;
    cards().forEach((card) => {
      const cardWalls = (card.dataset.walls || 'single').split(/\s+/).filter(Boolean);
      const cardSize = card.dataset.size || '';
      const keep =
        (walls.length === 0 || cardWalls.some((w) => walls.includes(w))) &&
        (sizes.length === 0 || sizes.includes(cardSize));
      card.hidden = !keep;
      if (keep) shown += 1;
    });

    facets.forEach((facet) => {
      const line = facet.querySelector('[data-facet-selected]');
      if (line) line.textContent = `${selectedValues(facet).length} selected`;
    });

    const anyActive = walls.length > 0 || sizes.length > 0;
    if (removeAll) removeAll.hidden = !anyActive;
    if (count) count.textContent = `${shown} product${shown === 1 ? '' : 's'}`;
  };

  facets.forEach((facet) => {
    facet.addEventListener('change', (e) => {
      if (e.target && e.target.matches('[data-facet-option]')) apply();
    });
    const reset = facet.querySelector('[data-facet-reset]');
    if (reset) {
      reset.addEventListener('click', () => {
        facet.querySelectorAll('[data-facet-option]').forEach((input) => {
          input.checked = false;
        });
        apply();
      });
    }
  });

  if (removeAll) {
    removeAll.addEventListener('click', () => {
      root.querySelectorAll('[data-facet-option]').forEach((input) => {
        input.checked = false;
      });
      apply();
    });
  }

  // A browser that restores form state on back/forward restores the ticks:
  // read them before painting rather than showing a grid they do not match.
  apply();
}

function init(root = document) {
  root.querySelectorAll('[data-shop-filters]').forEach(bind);
}

init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
